import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import styled from 'styled-components';
import Placeholder from '@tiptap/extension-placeholder';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { theme } from '../../theme/semanticTokens';
import { sanitizeRichTextHtml } from './richTextSanitize';
import { Toolbar } from './Toolbar';

function normalizeEmptyHtml(html: string): string {
  const trimmed = html.trim();
  if (trimmed === '') return '';
  if (/^<p>(<br\s*\/?>)?<\/p>$/i.test(trimmed)) return '';
  return trimmed;
}

export interface RichTextEditorRef {
  getHTML: () => string;
  clearContent: () => void;
}

export interface RichTextEditorProps {
  value?: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  minHeight?: string;
  autoFocus?: boolean;
  showEmojiInToolbar?: boolean;
}

const EditorWrapper = styled.div<{ $disabled?: boolean; $minHeight: string }>`
  border-radius: ${theme.dime.radius.m};
  border: 1px solid ${theme.dime.colors.border.static};
  background: ${theme.dime.colors.surface.primary};
  overflow: hidden;

  &:focus-within {
    border-color: ${theme.dime.colors.border.interactiveSelected};
  }

  ${({ $disabled }) =>
    $disabled
      ? `
    background-color: ${theme.dime.colors.fill.disabled};
    cursor: not-allowed;
    pointer-events: none;
  `
      : ''}

  .tiptap {
    padding: ${theme.dime.spacing.m} ${theme.dime.spacing.l};
    min-height: ${({ $minHeight }) => $minHeight};
    outline: none;
    font-family: ${theme.dime.fonts.families.roboto};
    font-size: 14px;
    line-height: 1.5;
    color: ${theme.dime.colors.text.primary};
    white-space: pre-wrap;
    word-wrap: break-word;

    p {
      margin: 0 0 4px 0;
    }

    p:last-child {
      margin-bottom: 0;
    }

    p.is-editor-empty:first-child::before {
      color: ${theme.dime.colors.text.disabled};
      content: attr(data-placeholder);
      float: left;
      height: 0;
      pointer-events: none;
    }
  }
`;

export const RichTextEditor = forwardRef<
  RichTextEditorRef,
  RichTextEditorProps
>(
  (
    {
      value = '',
      onChange,
      placeholder = '',
      disabled = false,
      readOnly = false,
      minHeight = '80px',
      autoFocus = false,
      showEmojiInToolbar = true
    },
    ref
  ) => {
    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: false,
          blockquote: false,
          codeBlock: false,
          code: false,
          horizontalRule: false
        }),
        Placeholder.configure({ placeholder })
      ],
      content: sanitizeRichTextHtml(value),
      editable: !disabled && !readOnly,
      autofocus: autoFocus,
      onUpdate: ({ editor: updatedEditor }) => {
        const html = updatedEditor.getHTML();
        onChange?.(normalizeEmptyHtml(html) === '' ? '' : html);
      }
    });

    useImperativeHandle(ref, () => ({
      getHTML: () => {
        const html = editor?.getHTML() ?? '';
        return normalizeEmptyHtml(html) === '' ? '' : html;
      },
      clearContent: () => editor?.commands.clearContent()
    }));

    useEffect(() => {
      const sanitized = sanitizeRichTextHtml(value);
      const currentHtml = editor?.getHTML() ?? '';
      if (
        editor &&
        normalizeEmptyHtml(sanitized) !== normalizeEmptyHtml(currentHtml)
      ) {
        editor.commands.setContent(sanitized);
      }
    }, [value, editor]);

    if (!editor) {
      return null;
    }

    return (
      <EditorWrapper $disabled={disabled} $minHeight={minHeight}>
        {!disabled && !readOnly && (
          <Toolbar editor={editor} showEmojiInToolbar={showEmojiInToolbar} />
        )}
        <EditorContent editor={editor} />
      </EditorWrapper>
    );
  }
);

RichTextEditor.displayName = 'RichTextEditor';
